import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import {
  ComboWeatherSnapshot,
  OperatorSpraySessionCombo,
  SprayMachine,
  SprayOperator,
  SpraySession,
  SprayProduct,
} from '../../services/sprayService';
import { resolveComboIntensity } from '../../utils/resolveComboIntensity';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatTime as formatTimeShared } from '../../dateFormat';
import BackCapturePill from './BackCapturePill';
import './ComboSummaryList.css';

export interface ComboSummaryListProps {
  combos: OperatorSpraySessionCombo[];
  operators: SprayOperator[];
  machines: SprayMachine[];
  /** Looked up to resolve productId → productName in the consumption list. */
  products?: SprayProduct[];
  /**
   * Parent session passed through so the expanded body can resolve
   * per-combo intensity with a lazy fallback to the parent's deprecated
   * `intensityLevel` (Phase 6b — multi_operator_spray_phase6b).
   * Optional during the migration window; callers without the parent in
   * hand (legacy fixtures, isolated tests) get the combo-only resolution.
   */
  parentSession?: SpraySession;
  /**
   * When the parent session has `legacyAttribution === 'single-operator'`,
   * render a small footnote on each row to signal it's migrated data.
   */
  legacyAttribution?: 'single-operator';
  /**
   * Optional slot rendered next to each row's action area — Task 5.5 will
   * inject ComboActionMenu here for manager surfaces. Read-only callers
   * (operator detail) pass nothing.
   */
  renderRowActions?: (combo: OperatorSpraySessionCombo) => React.ReactNode;
}

/**
 * Status → glyph mapping. Intentionally NOT i18n strings — the glyphs are
 * pure typography and read the same in every locale.
 */
const STATUS_GLYPHS: Record<OperatorSpraySessionCombo['status'], string> = {
  not_started: '○',
  in_progress: '◐',
  completed:   '●',
  incomplete:  '◓',
  cancelled:   '✕',
};

/**
 * Build a Set<machineId> for machines that appear on 2+ combos. Used to
 * append the "(shared)" suffix in the handover label.
 */
function buildSharedMachineSet(combos: OperatorSpraySessionCombo[]): Set<string> {
  const counts = new Map<string, number>();
  for (const c of combos) {
    if (!c.machineId) continue;
    counts.set(c.machineId, (counts.get(c.machineId) ?? 0) + 1);
  }
  const shared = new Set<string>();
  counts.forEach((count, id) => {
    if (count >= 2) shared.add(id);
  });
  return shared;
}

const ComboSummaryList: React.FC<ComboSummaryListProps> = ({
  combos,
  operators,
  machines,
  products,
  parentSession,
  legacyAttribution,
  renderRowActions,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const sharedMachineIds = useMemo(() => buildSharedMachineSet(combos), [combos]);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resolveOperatorName = (id: string): string => {
    const op = operators.find(o => o._id === id);
    return op?.name ?? t('spray.sessions.combos.unknownOperator');
  };

  const resolveMachineLabel = (id: string): string => {
    const machine = machines.find(m => m._id === id);
    if (!machine) return t('spray.sessions.combos.unknownMachine');
    if (sharedMachineIds.has(id)) {
      return `${machine.name} ${t('spray.sessions.combos.sharedSuffix')}`;
    }
    return machine.name;
  };

  const resolveProductName = (id: string): string => {
    const p = products?.find(prod => prod._id === id);
    return p?.productName ?? id;
  };

  // Audit-trail `by` is a User._id reference. Resolve via SprayOperator.userId
  // — the operator-driven lifecycle actions (start, complete, end-early,
  // abandon) attribute to the operator who took them. Manager-driven actions
  // won't have an operator linkage; we surface a translated "Manager" fallback
  // rather than the raw ObjectId, which was meaningless to the user.
  const resolveActorName = (userId: string | undefined): string => {
    if (!userId) return t('spray.sessions.combos.unknownActor');
    const op = operators.find(o => o.userId === userId);
    if (op) return op.name;
    return t('spray.sessions.combos.managerActor');
  };

  // Compose a one-dimension weather string from the categorical + numeric
  // sources stored on a snapshot. Returns '—' when no value is recorded.
  // `unitPrefix` is the string that goes between the numeric reading and
  // the unit token — empty for temperature (e.g. `22°C`), a single space
  // for wind / precipitation (e.g. `12 km/h`).
  const describeWeatherDimension = (
    cat: string | undefined,
    num: number | undefined,
    unitPrefix: string,
    unit: string,
  ): string => {
    if (cat && num != null) return `${cat} (${num}${unitPrefix}${unit})`;
    if (cat) return cat;
    if (num != null) return `${num}${unitPrefix}${unit}`;
    return '—';
  };

  const describeTemp = (snap: ComboWeatherSnapshot | undefined): string => {
    if (!snap) return '—';
    const cat = snap.temperatureCategory ?? snap.finalSelected?.temperature;
    const num = snap.systemReported?.temp ?? snap.temperature;
    return describeWeatherDimension(cat, num, '', t('spray.sessions.combos.temperatureUnitC'));
  };

  const describeWind = (snap: ComboWeatherSnapshot | undefined): string => {
    if (!snap) return '—';
    const cat = snap.windCategory ?? snap.finalSelected?.wind;
    const num = snap.systemReported?.windSpeed ?? snap.windSpeed;
    return describeWeatherDimension(cat, num, ' ', t('spray.sessions.combos.windUnitKmh'));
  };

  const describePrecip = (snap: ComboWeatherSnapshot | undefined): string => {
    if (!snap) return '—';
    const cat = snap.precipitationCategory ?? snap.finalSelected?.precipitation;
    const num = snap.systemReported?.precipitation ?? snap.precipitation;
    return describeWeatherDimension(cat, num, '', '');
  };

  const isWeatherEmpty = (snap: ComboWeatherSnapshot | undefined): boolean =>
    describeTemp(snap) === '—' && describeWind(snap) === '—' && describePrecip(snap) === '—';

  // Machine-local 24h time via the shared dateFormat chokepoint. This is an
  // intentional behaviour change from the old raw substring(11,16) read
  // (which showed the ISO string's own UTC-offset digits verbatim); no farm
  // timezone is available in this component's props, so machine-local is
  // the documented replacement (track office-app-spray-migration Task 7).
  const formatTime = (iso?: string): string => {
    if (!iso) return '—';
    return formatTimeShared(iso);
  };

  // Translate a resolved intensity label to the locale string. The
  // resolver shape returns `'medium/heavy'` for the historic compound
  // level — map that to the camelCase i18n key the existing option
  // dictionary uses. Returns the missing-value placeholder when the
  // resolver yields null (neither combo nor parent has a value).
  const renderIntensityValue = (combo: OperatorSpraySessionCombo): string => {
    const label = resolveComboIntensity(combo, parentSession);
    if (!label) return '—';
    const key = label === 'medium/heavy' ? 'mediumHeavy' : label;
    return t(`spray.sessions.combos.intensity.options.${key}`);
  };

  return (
    <div className="combo-summary-list" data-testid="combo-summary-list">
      {combos.map(combo => {
        const isOpen = expanded.has(combo._id);
        const operatorName = resolveOperatorName(combo.operatorId);
        const machineLabel = resolveMachineLabel(combo.machineId);
        const glyph = STATUS_GLYPHS[combo.status];
        const statusLabelKey =
          combo.status === 'not_started' ? 'notStarted'
          : combo.status === 'in_progress' ? 'inProgress'
          : combo.status;
        const statusLabel = t(`spray.sessions.combos.status.${statusLabelKey}`);

        return (
          <div
            key={combo._id}
            data-testid={`combo-row-${combo._id}`}
            className="pat-row-card combo-summary-list__row mb-2"
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                data-testid="combo-status-icon"
                role="img"
                aria-label={statusLabel}
                className="combo-summary-list__status-icon"
              >
                {glyph ?? '?'}
              </span>
              <span className="combo-summary-list__operator">{operatorName}</span>
              <span className="combo-summary-list__separator">·</span>
              <span className="combo-summary-list__machine">{machineLabel}</span>

              {combo.startedAt && (
                <span className="combo-summary-list__timing ms-2">
                  {t('spray.sessions.combos.timingLine', {
                    start: formatTime(combo.startedAt),
                    end: formatTime(combo.endedAt),
                  })}
                </span>
              )}

              {combo.captureMode === 'back_capture' && (
                <span className="combo-summary-list__pill ms-2">
                  <BackCapturePill
                    captureMode={combo.captureMode}
                    backCaptureBeyondLimit={combo.backCaptureBeyondLimit}
                  />
                </span>
              )}

              <div className="ms-auto d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="pat-btn pat-btn--subtle"
                  data-testid={`combo-expand-${combo._id}`}
                  aria-expanded={isOpen}
                  aria-label={t(
                    isOpen ? 'spray.sessions.combos.collapse' : 'spray.sessions.combos.expand',
                  )}
                  onClick={() => toggle(combo._id)}
                >
                  {isOpen ? '−' : '+'}
                </button>
                {renderRowActions && (
                  <span className="combo-summary-list__row-actions">
                    {renderRowActions(combo)}
                  </span>
                )}
              </div>
            </div>

            {isOpen && (
              <div className="combo-summary-list__body mt-2">
                {/* Per-combo intensity (Phase 6b). Reads `combo.intensityLevel`
                    with a lazy fallback to the deprecated parent session's
                    `intensityLevel` via `resolveComboIntensity`. Renders the
                    missing-value placeholder when neither is set. */}
                <div
                  className="combo-summary-list__intensity mb-2"
                  data-testid="combo-intensity"
                >
                  <span className="combo-summary-list__intensity-label">
                    {t('spray.sessions.combos.intensity.summary.label')}:{' '}
                  </span>
                  <span>{renderIntensityValue(combo)}</span>
                </div>

                {/* Weather. Renders categorical + numeric for both start
                    and end snapshots. Falls back to '—' per dimension when
                    only one shape (categorical or numeric) is present. */}
                <div className="combo-summary-list__weather mb-2">
                  <div className="combo-summary-list__section-label">
                    {t('spray.sessions.combos.weather')}
                  </div>
                  <dl className="row mb-0">
                    <dt className="col-sm-3">
                      {t('spray.sessions.combos.weather')} ({t('spray.sessions.combos.weatherStart').toLowerCase()})
                    </dt>
                    <dd className="col-sm-9">
                      {isWeatherEmpty(combo.weatherSnapshots?.start)
                        ? '—'
                        : t('spray.sessions.combos.weatherSummaryFormat', {
                            temperatureLabel: t('spray.sessions.combos.temperatureLabel'),
                            temperature: describeTemp(combo.weatherSnapshots?.start),
                            windLabel: t('spray.sessions.combos.windLabel'),
                            wind: describeWind(combo.weatherSnapshots?.start),
                            precipitationLabel: t('spray.sessions.combos.precipitationLabel'),
                            precipitation: describePrecip(combo.weatherSnapshots?.start),
                          })}
                    </dd>
                    <dt className="col-sm-3">
                      {t('spray.sessions.combos.weather')} ({t('spray.sessions.combos.weatherEnd').toLowerCase()})
                    </dt>
                    <dd className="col-sm-9">
                      {isWeatherEmpty(combo.weatherSnapshots?.end)
                        ? '—'
                        : t('spray.sessions.combos.weatherSummaryFormat', {
                            temperatureLabel: t('spray.sessions.combos.temperatureLabel'),
                            temperature: describeTemp(combo.weatherSnapshots?.end),
                            windLabel: t('spray.sessions.combos.windLabel'),
                            wind: describeWind(combo.weatherSnapshots?.end),
                            precipitationLabel: t('spray.sessions.combos.precipitationLabel'),
                            precipitation: describePrecip(combo.weatherSnapshots?.end),
                          })}
                    </dd>
                  </dl>
                </div>

                {/* Products consumed */}
                {combo.productsConsumed && combo.productsConsumed.length > 0 && (
                  <div className="combo-summary-list__products mb-2">
                    <div className="combo-summary-list__section-label">
                      {t('spray.sessions.combos.products')}
                    </div>
                    <ul className="combo-summary-list__list">
                      {combo.productsConsumed.map((p, i) => (
                        <li key={`${combo._id}-prod-${i}`}>
                          {/* Label with the row's own quantityUnit (the field the
                              backend actually writes; `unit` is a legacy fallback)
                              and rescale to a readable unit via formatMeasure. */}
                          {resolveProductName(p.productId)} — {formatMeasure(p.quantity, p.quantityUnit ?? p.unit)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tank count */}
                <div className="combo-summary-list__tanks mb-2">
                  <span className="combo-summary-list__tanks-label">
                    {t('spray.sessions.combos.tanks')}:{' '}
                  </span>
                  <span>{combo.tankCount ?? '—'}</span>
                </div>

                {/* PPE */}
                {combo.ppeChecklist && combo.ppeChecklist.length > 0 && (
                  <div className="combo-summary-list__ppe mb-2">
                    <div className="combo-summary-list__section-label">
                      {t('spray.sessions.combos.ppe')}
                    </div>
                    {combo.ppeChecklist.map((item, i) => (
                      <Form.Check
                        key={`${combo._id}-ppe-${i}`}
                        type="checkbox"
                        readOnly
                        disabled
                        checked={item.checked}
                        label={item.item}
                      />
                    ))}
                  </div>
                )}

                {/* Audit trail. `entry.by` is a User._id; resolve to operator
                    name via SprayOperator.userId, with a translated "Manager"
                    fallback for non-operator actors. */}
                {combo.auditTrail && combo.auditTrail.length > 0 && (
                  <div className="combo-summary-list__audit mb-2">
                    <div className="combo-summary-list__section-label">
                      {t('spray.sessions.combos.audit')}
                    </div>
                    <ul className="combo-summary-list__list">
                      {combo.auditTrail.map((entry, i) => (
                        <li key={`${combo._id}-audit-${i}`}>
                          {t('spray.sessions.combos.auditEntryFormat', {
                            action: entry.action,
                            by: resolveActorName(entry.by),
                            time: formatTime(entry.at),
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {legacyAttribution === 'single-operator' && (
              <div className="combo-summary-list__footnote mt-1">
                {t('spray.sessions.combos.legacyAttributionFootnote')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ComboSummaryList;
